/**
 * Dashboard Component
 *
 * Main dashboard view for facility asset management displaying:
 * - Key performance metrics (health scores, budgets, problematic locations)
 * - Geographic portfolio health heatmap
 * - Predictive spending chart with 10-year asset replacement projections
 * - Toggle between cost and count views for spending data
 * - Caching support for improved performance
 *
 * Features:
 * - Real-time data fetching with cache fallback
 * - Interactive heatmap with location health visualization
 * - Responsive grid layout for mobile/desktop
 * - Error handling and loading states
 * - Refresh functionality for real-time updates
 */

import { useDashboardCache } from "@/hooks/use-dashboard-cache";
import { Loader } from "@/layouts/signed-in/Loader/Loader";
import { getDashboardData } from "@/services/asset-service";
import { useUserStore } from "@/stores/user-store";
import { CACHE_KEYS, dashboardCache } from "@/utils/dashboard-cache";
import { Button } from "antd";
import {
	useCallback,
	useEffect,
	useMemo,
	useReducer,
	useRef,
} from "react";
import "./Dashboard.scss";
import type {
	CoreDashboardAnalytics,
	DashboardAssetAggregate,
} from "@/models/organization/dashboard-analytics/dashboard-analytics.model";
import { MinusOutlined, PlusOutlined, ReloadOutlined, UndoOutlined } from "@ant-design/icons";
import { AiOutlineNumber } from "react-icons/ai";
import { BsCurrencyDollar, BsHourglass } from "react-icons/bs";
import { GiHealthNormal } from "react-icons/gi";
import { GoogleHeatmap, GoogleHeatmapRef } from "./google-heat-map";
// Health score color thresholds
const HEALTH_COLORS = {
	GOOD: "#198754", // Green for 70%+
	WARNING: "#FFC107", // Yellow for 50-69%
	DANGER: "#dc3545", // Red for <50%
};

/**
 * Dashboard component state interface extending core analytics data
 */
interface DashboardState extends CoreDashboardAnalytics {
	isLoading: boolean;
	error: string | null;
}

/**
 * Dashboard reducer action types for state management
 */
type DashboardAction =
	| {
			type: "SET_DATA";
			payload: CoreDashboardAnalytics;
	  }
	| { type: "SET_LOADING"; payload: boolean }
	| { type: "SET_ERROR"; payload: string | null }
	| { type: "BULK_SET"; payload: Partial<DashboardState> };

/**
 * Initial dashboard state with loading enabled and empty data structures
 */
const initialState: DashboardState = {
	locations: [],
	analytics: {
		healthScore: 0,
		dollarAmountPastUsefulLife: 0,
		totalBudget: 0,
	},
	assets: [],
	projectedSpendingChartData: {
		yearsWithAssets: [],
		assetTypes: [],
		assetValuesByYearAndType: {},
		assetCountByYearAndType: {},
		totalBudget: 0,
		hasEstimatedDates: false,
	},
	isLoading: true, // Start with loading true since we always need to fetch data
	error: null,
};

/**
 * Dashboard State Reducer
 * Manages dashboard state including loading, error, and data states
 */
const dashboardReducer = (
	state: DashboardState,
	action: DashboardAction,
): DashboardState => {
	switch (action.type) {
		case "SET_DATA":
			// Successfully loaded dashboard data
			return {
				...state,
				locations: action.payload.locations,
				analytics: action.payload.analytics,
				assets: action.payload.assets,
				projectedSpendingChartData: action.payload.projectedSpendingChartData,
				isLoading: false,
				error: null,
			};
		case "SET_LOADING":
			// Update loading state
			return {
				...state,
				isLoading: action.payload,
			};
		case "SET_ERROR":
			// Handle error state - only stop loading if there's an actual error
			return {
				...state,
				error: action.payload,
				isLoading: action.payload ? false : state.isLoading,
			};
		case "BULK_SET":
			// Bulk update multiple state properties
			return {
				...state,
				...action.payload,
			};
		default:
			return state;
	}
};

/**
 * Dashboard Component
 * Main dashboard view displaying organization analytics, health scores,
 * location heatmap, and predictive spending chart
 */
const Dashboard = () => {
	// References
	const mapRef = useRef<GoogleHeatmapRef>(null);
	const isFetchingRef = useRef(false); // Prevents duplicate API calls

	// User and organization data
	const user = useUserStore((state) => state.user);
	const organization_id = user?.organization_id ?? user?.organization?.id ?? 0;

	// Dashboard state management
	const [state, dispatch] = useReducer(dashboardReducer, initialState);
	const { locations, analytics, isLoading, error } = state;

	// Initialize cache management
	useDashboardCache();

	// Computed values
	const memoizedPoorHealthLocations = useMemo(
		() => locations.filter((loc) => (loc.health?.healthScore ?? 0) < 60).length,
		[locations],
	);

	const locationsMissingCoordinates = useMemo(
		() => locations.filter(
			(loc) =>
				typeof loc.latitude !== "number" ||
				typeof loc.longitude !== "number" ||
				Number.isNaN(loc.latitude) ||
				Number.isNaN(loc.longitude),
		).length,
		[locations],
	);

	/**
	 * Get color based on health score value
	 * @param score Health score (0-100)
	 * @returns Color hex code
	 */
	const getHealthScoreColor = (score: number) => {
		if (score >= 70) return HEALTH_COLORS.GOOD;
		if (score >= 50) return HEALTH_COLORS.WARNING;
		return HEALTH_COLORS.DANGER;
	};

	/**
	 * Get color based on percentage of locations in poor health
	 * @param poorCount Number of locations with poor health
	 * @param total Total number of locations
	 * @returns Color hex code
	 */
	const getPoorLocationsColor = (poorCount: number, total: number) => {
		if (total === 0) return HEALTH_COLORS.GOOD;
		const percent = (poorCount / total) * 100;
		if (percent < 30) return HEALTH_COLORS.GOOD;
		if (percent < 50) return HEALTH_COLORS.WARNING;
		return HEALTH_COLORS.DANGER;
	};

	/**
	 * Fetch dashboard analytics data with caching support
	 * @param forceRefresh Skip cache and fetch fresh data
	 */
	const fetchDashboardData = useCallback(
		async (forceRefresh: boolean = false) => {
			if (!organization_id) return;

			// Prevent duplicate concurrent calls
			if (isFetchingRef.current && !forceRefresh) {
				return;
			}

			try {
				isFetchingRef.current = true;
				dispatch({ type: "SET_LOADING", payload: true });
				dispatch({ type: "SET_ERROR", payload: null });

				// Check cache first (skip if force refresh requested)
				if (!forceRefresh) {
					const cachedData = dashboardCache.get<CoreDashboardAnalytics>(
						organization_id,
						CACHE_KEYS.DASHBOARD_ANALYTICS,
					);

					if (cachedData) {
						// Load cached data instantly
						dispatch({
							type: "SET_DATA",
							payload: cachedData,
						});
						isFetchingRef.current = false;
						return;
					}
				}

				// Fetch fresh data from API
				const data = await getDashboardData();

				// Process location data with health scores
				const processedLocations = (data.locations || []).map(
					(location: any) => ({
						...location,
						health: {
							healthScore:
								location.healthScore?.healthScore ??
								location.health_score?.health_score ??
								0,
							locationId: location.id,
						},
					}),
				);

				// Process analytics data with proper number formatting
				const analytics = {
					healthScore: Number(data.analytics?.healthScore) || 0,
					dollarAmountPastUsefulLife:
						Number(data.analytics?.dollarAmountPastUsefulLife) || 0,
					totalBudget: Number(data.analytics?.totalBudget) || 0,
				};

				// Process predictive spending chart data
				const processedChartData = {
					yearsWithAssets:
						data.projectedSpendingChartData?.yearsWithAssets || [],
					assetTypes: data.projectedSpendingChartData?.assetTypes || [],
					assetValuesByYearAndType:
						data.projectedSpendingChartData?.assetValuesByYearAndType || {},
					assetCountByYearAndType:
						data.projectedSpendingChartData?.assetCountByYearAndType || {},
					totalBudget: data.projectedSpendingChartData?.totalBudget || 0,
					hasEstimatedDates:
						data.projectedSpendingChartData?.hasEstimatedDates || false,
				};

				// Combine all processed data
				const processedData: CoreDashboardAnalytics = {
					locations: processedLocations,
					analytics,
					assets: (data.assets as DashboardAssetAggregate[]) || [],
					projectedSpendingChartData: processedChartData,
				};

				// Cache the processed data for future requests
				dashboardCache.set(
					organization_id,
					CACHE_KEYS.DASHBOARD_ANALYTICS,
					processedData,
				);

				// Update component state with new data
				dispatch({
					type: "SET_DATA",
					payload: processedData,
				});
			} catch (error) {
				// Handle API errors
				dispatch({
					type: "SET_ERROR",
					payload: "Error fetching dashboard data",
				});
				console.error("Error fetching dashboard data:", error);
			} finally {
				// Always reset fetching flag
				isFetchingRef.current = false;
			}
		},
		[organization_id],
	);

	/**
	 * Handle refresh button click - force refresh data from API
	 */
	const handleRefreshData = useCallback(() => {
		fetchDashboardData(true);
	}, [fetchDashboardData]);

	// Initialize data fetching when organization is available
	useEffect(() => {
		if (organization_id && organization_id > 0) {
			// Valid organization - fetch dashboard data
			fetchDashboardData();
		} else if (user && (!organization_id || organization_id === 0)) {
			// User loaded but no valid organization ID - show error
			dispatch({ type: "SET_LOADING", payload: false });
			dispatch({ type: "SET_ERROR", payload: "No organization found" });
		}
		// If user is not loaded yet, keep initial loading state
	}, [organization_id, user, fetchDashboardData]);

	// Show loading spinner centered across viewport while data is being fetched
	if (isLoading || !organization_id || organization_id === 0) {
		return (
			<div className="loading-container">
				<Loader />
			</div>
		);
	}

	// Show error state if data fetching failed
	if (error) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="text-center">
					<h2 className="text-red-500 mb-4">Error Loading Dashboard</h2>
					<p>{error}</p>
				</div>
			</div>
		);
	}

	// Format currency helper
	const formatCurrency = (value: string | number) => {
		const numValue = Number(value);
		if (Number.isNaN(numValue)) return "$0";
		if (numValue >= 1000000) return `$${(numValue / 1000000).toFixed(1)}M`;
		if (numValue >= 1000) return `$${(numValue / 1000).toFixed(0)}K`;
		return `$${numValue.toFixed(0)}`;
	};

	// Main dashboard render
	return (
		<div className="dashboard-container">
			{/* Full-size map with floating stat cards */}
			<div className="dashboard-map-area" style={{ position: "relative" }}>
				<GoogleHeatmap ref={mapRef} locations={locations} />

				{locationsMissingCoordinates > 0 && (
					<div style={{
						position: "absolute",
						bottom: 12,
						left: 12,
						zIndex: 10,
						background: "rgba(255, 251, 230, 0.95)",
						border: "1px solid #ffe58f",
						borderRadius: "6px",
						padding: "6px 12px",
						fontSize: "12px",
						color: "#8c6d1f",
						maxWidth: "320px",
					}}>
						{locationsMissingCoordinates} of {locations.length} locations hidden — missing GPS coordinates
					</div>
				)}

				{/* Floating stat tiles - right side */}
				<div className="dashboard-floating-stats" style={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
					<div className="floating-stat-card">
						<div className="stat-icon" style={{ color: getHealthScoreColor(analytics.healthScore ?? 0) }}>
							<GiHealthNormal />
						</div>
						<div className="stat-info">
							<span className="stat-value">{Number(analytics.healthScore ?? 0).toFixed(1)}%</span>
							<span className="stat-label">Health Score</span>
						</div>
					</div>

					<div className="floating-stat-card">
						<div className="stat-icon" style={{ color: getPoorLocationsColor(memoizedPoorHealthLocations, locations.length) }}>
							<AiOutlineNumber />
						</div>
						<div className="stat-info">
							<span className="stat-value">{memoizedPoorHealthLocations} / {locations.length}</span>
							<span className="stat-label">Poor Health</span>
						</div>
					</div>

					<div className="floating-stat-card">
						<div className="stat-icon" style={{ color: "#0d6efd" }}>
							<BsCurrencyDollar />
						</div>
						<div className="stat-info">
							<span className="stat-value">{formatCurrency(analytics.totalBudget ?? 0)}</span>
							<span className="stat-label">10-Yr Budget</span>
						</div>
					</div>

					<div className="floating-stat-card">
						<div className="stat-icon" style={{ color: "#6c757d" }}>
							<BsHourglass />
						</div>
						<div className="stat-info">
							<span className="stat-value">{formatCurrency(analytics.dollarAmountPastUsefulLife ?? 0)}</span>
							<span className="stat-label">Past Useful Life</span>
						</div>
					</div>
				</div>

				{/* Map controls */}
				<div className="dashboard-map-controls" style={{ position: "absolute", bottom: 16, left: 16, zIndex: 10 }}>
					<Button
						icon={<PlusOutlined />}
						onClick={() => mapRef.current?.zoomIn()}
						className="map-control-btn"
					/>
					<Button
						icon={<MinusOutlined />}
						onClick={() => mapRef.current?.zoomOut()}
						className="map-control-btn"
					/>
					<div className="map-control-divider" />
					<Button
						icon={<UndoOutlined />}
						onClick={() => mapRef.current?.resetView()}
						className="map-control-btn"
					/>
					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefreshData}
						className="map-control-btn"
					/>
				</div>
			</div>

		</div>
	);
};

export { Dashboard };
